import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { Copy, MoreVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDuplicateTaxonomyList, type TaxonomyListSummary } from '@/entities/taxonomy-list';

interface ListActionMenuProps {
  list: TaxonomyListSummary;
  onDelete: () => void;
}

/**
 * Row actions for a shared vocabulary.
 *
 * Delete only RAISES intent — the page owns the confirm. A dialog rendered in
 * here would be unmounted by its own success (deleting the list drops the
 * row), and a Radix dialog that unmounts while open never restores
 * `body { pointer-events }`, silently freezing the whole page.
 *
 * Duplicate stays local: it adds a row rather than removing this one, so
 * nothing unmounts underneath it.
 */
export function ListActionMenu({ list, onDelete }: ListActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const duplicate = useDuplicateTaxonomyList();

  return (
    // Portals bubble events through the REACT tree, not the DOM tree, so
    // without this the menu's own clicks reach the row and navigate into it.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.listActionMenu.actionsFor', { name: list.name })}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            disabled={duplicate.isPending}
            onSelect={() => duplicate.mutate(list.id)}
          >
            <Copy className="size-4" />
            {t('taxonomy.listActionMenu.duplicate')}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
            <Trash2 className="size-4" />
            {t('taxonomy.listActionMenu.deleteList')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

ListActionMenu.displayName = 'ListActionMenu';
